// ResponseUI/PreviewTesterModal.jsx
// === STEP 5: FINAL REFINEMENT ===
//
// Changes vs your current file:
//   - Removed `previewTesterKey` state (was causing PreviewTester remounts)
//   - Key is now stable per statusCode
//   - getInitialTemplateForPreview only generates templates for:
//       • root arrays (Case 1)
//       • flat objects (no nested arrays) — Case 2
//       • primitives (string / number / boolean) — literal Case 2 template
//   - For objects WITH nested arrays: returns DEFAULT so LeftPanel
//     can show the Single vs Multiple popup
//   - null returns DEFAULT so validator flags `unclassified`

import React, { useState, useEffect, useCallback, useRef } from "react";
import PreviewTester from "../../PreviewTesterHome/PreviewTester/PreviewTester";
import styles from "./ResponseTemplateEditor.module.css";
import { generateTemplateFromJSON } from "../../PreviewTesterHome/PreviewTester/utils/templateGenerator";

const DEFAULT_TEMPLATE = '{{ response_data | dump | safe }}';

/**
 * PreviewTesterModal - Embeds the full Preview Tester component
 * for creating response templates within the API Config flow.
 *
 * IMPORTANT: This does NOT save to the backend directly.
 * It collects the template data and passes it back to the parent.
 */
const PreviewTesterModal = ({
  isOpen,
  onClose,
  onTemplateDataReady,
  sampleResponse,
  statusCode,
  kbId,
  appData,
  initialTemplate = null,
}) => {
  const [templateData, setTemplateData] = useState({
    resp_template: initialTemplate || {},
    controls: {},
    transformation_steps: [],
    templateString: initialTemplate || '',
  });
  const [isTemplateReady, setIsTemplateReady] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const isClosingAfterUse = useRef(false);
  const isInitialized = useRef(false);

  const modalRef = useRef(null);
  const previewTesterRef = useRef(null);

  // ============================================================
  // Reset on modal open — key is now stable per statusCode
  // ============================================================
  useEffect(() => {
    if (isOpen) {
      isClosingAfterUse.current = false;

      if (!isInitialized.current) {
        isInitialized.current = true;
        setTemplateData({
          resp_template: initialTemplate || {},
          controls: {},
          transformation_steps: [],
          templateString: initialTemplate || '',
        });
        setIsTemplateReady(false);
      }
    } else {
      isInitialized.current = false;
      setIsClosing(false);
    }
  }, [isOpen, initialTemplate]);

  // ============================================================
  // Template data from Controls
  // ============================================================
  const handleTemplateDataReady = useCallback((data) => {
    console.log('[PreviewTesterModal] Template data ready:', data);
    setTemplateData({
      resp_template: data.resp_template || data.templateString || {},
      controls: data.controls || {},
      transformation_steps: data.transformation_steps || [],
      templateString: data.templateString || data.resp_template || '',
    });
    setIsTemplateReady(true);

    if (onTemplateDataReady) {
      onTemplateDataReady({ ...data, statusCode });
    }
  }, [onTemplateDataReady, statusCode]);

  // ============================================================
  // Preview Tester save event
  // ============================================================
  const handlePreviewTesterSave = useCallback(
    (renderedPreview, templateString, steps, controls) => {
      console.log('[PreviewTesterModal] Preview Tester save event');

      const data = {
        resp_template: templateString || renderedPreview || {},
        controls: controls || {},
        transformation_steps: steps || [],
        templateString: templateString || renderedPreview || '',
      };

      setTemplateData(data);
      setIsTemplateReady(true);

      if (onTemplateDataReady) {
        onTemplateDataReady(data);
      }
    },
    [onTemplateDataReady]
  );

  // ============================================================
  // Preview Tester state changes
  // ============================================================
  const handlePreviewTesterChange = useCallback((data) => {
    if (data && data.templateString) {
      setTemplateData((prev) => ({
        ...prev,
        templateString: data.templateString,
        resp_template: data.templateString || prev.resp_template,
      }));
      setIsTemplateReady(true);
    }
  }, []);

  // ============================================================
  // Close handling
  // ============================================================
  const handleClose = () => {
    if (isClosing) return;

    if (isTemplateReady) {
      if (
        window.confirm(
          'You have a template designed. Are you sure you want to close?'
        )
      ) {
        setIsClosing(true);
        isClosingAfterUse.current = false;
        onClose();
      }
    } else {
      setIsClosing(true);
      isClosingAfterUse.current = false;
      onClose();
    }
  };

  // ============================================================
  // Use Template
  // ============================================================
  const handleUseTemplate = () => {
    if (templateData.templateString) {
      console.log(
        '[PreviewTesterModal] ✅ Using template, transformation_steps:',
        templateData.transformation_steps?.length || 0
      );
      isClosingAfterUse.current = true;

      if (onTemplateDataReady) {
        onTemplateDataReady({ ...templateData, statusCode });
      }

      setIsClosing(true);
      onClose();
    } else {
      alert('Please design a template first using the Preview Tester.');
    }
  };

  // ============================================================
  // Escape key
  // ============================================================
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen && !isClosing) {
        if (isTemplateReady) {
          if (
            window.confirm(
              'You have a template designed. Are you sure you want to close?'
            )
          ) {
            setIsClosing(true);
            isClosingAfterUse.current = false;
            onClose();
          }
        } else {
          setIsClosing(true);
          isClosingAfterUse.current = false;
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, isTemplateReady, isClosing]);

  // ============================================================
  // Click outside
  // ============================================================
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target) &&
        isOpen &&
        !isClosing
      ) {
        const previewTester = modalRef.current.querySelector(
          '.preview-tester-wrapper'
        );
        if (previewTester && previewTester.contains(e.target)) return;

        if (isTemplateReady) {
          if (
            window.confirm(
              'You have a template designed. Are you sure you want to close?'
            )
          ) {
            setIsClosing(true);
            isClosingAfterUse.current = false;
            onClose();
          }
        } else {
          setIsClosing(true);
          isClosingAfterUse.current = false;
          onClose();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, isTemplateReady, isClosing]);

  // ============================================================
  // getInitialTemplateForPreview
  //
  // Generates templates for:
  //   • root arrays           → Case 1 loop template
  //   • flat objects          → Case 2 field template
  //   • primitives            → literal Case 2 template (string form)
  //
  // Returns DEFAULT for:
  //   • objects with arrays   → let LeftPanel show layout popup
  //   • null / empty / invalid → let validator flag `unclassified`
  // ============================================================
   // ============================================================
  // getInitialTemplateForPreview
  //
  // Generates templates for:
  //   • root arrays           → delegated to generateTemplateFromJSON
  //   • flat objects          → delegated to generateTemplateFromJSON
  //   • primitives            → literal Case 2 template (string form)
  //
  // Returns DEFAULT for:
  //   • objects with arrays   → let LeftPanel show layout popup
  //   • null / empty / invalid → let validator flag `unclassified`
  // ============================================================
  const getInitialTemplateForPreview = () => {
    console.log('[PreviewTesterModal] 🎨 getInitialTemplateForPreview CALLED');
    console.log('[PreviewTesterModal] 🎨 initialTemplate prop:', initialTemplate);
    console.log(
      '[PreviewTesterModal] 🎨 sampleResponse length:',
      sampleResponse?.length
    );
    console.log(
      '[PreviewTesterModal] 🎨 sampleResponse preview:',
      sampleResponse?.substring(0, 100)
    );

    // Only trust an explicit initialTemplate if it's not the default
    const isExplicitTemplate =
      initialTemplate &&
      initialTemplate !== DEFAULT_TEMPLATE &&
      typeof initialTemplate === 'string' &&
      initialTemplate.trim() !== '';

    if (isExplicitTemplate) {
      console.log(
        '[PreviewTesterModal] 🎨 Branch: explicit initialTemplate (non-default)'
      );
      return typeof initialTemplate === 'string'
        ? initialTemplate
        : JSON.stringify(initialTemplate, null, 2);
    }

    console.log(
      '[PreviewTesterModal] 🎨 Skipping default initialTemplate — will analyze sample'
    );

    if (!sampleResponse || !sampleResponse.trim()) {
      console.log('[PreviewTesterModal] 🎨 No sampleResponse — DEFAULT');
      return DEFAULT_TEMPLATE;
    }

    let parsed;
    try {
      parsed = JSON.parse(sampleResponse);
    } catch (e) {
      console.log('[PreviewTesterModal] 🎨 Invalid JSON — DEFAULT');
      return DEFAULT_TEMPLATE;
    }

    // ============================================================
    // NULL → unclassified → DEFAULT
    // ============================================================
    if (parsed === null) {
      console.log('[PreviewTesterModal] 🎨 Response is null — DEFAULT');
      return DEFAULT_TEMPLATE;
    }

    // ============================================================
    // PRIMITIVE (string / number / boolean) → literal template
    // ============================================================
    if (
      typeof parsed === 'string' ||
      typeof parsed === 'number' ||
      typeof parsed === 'boolean'
    ) {
      const literal = String(parsed);
      console.log(
        '[PreviewTesterModal] 🔤 Primitive response — using literal template:',
        literal
      );
      return literal;
    }

    // ============================================================
    // ROOT ARRAY → Case 1 loop template
    //
    // Delegate to generateTemplateFromJSON, which correctly handles
    // nested arrays (e.g. guests) and nested objects inside each
    // array item. It uses 'item' as the loop variable, matching
    // the shape this modal used to produce by hand.
    // ============================================================
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) {
        console.log('[PreviewTesterModal] 🎨 Empty array — DEFAULT');
        return DEFAULT_TEMPLATE;
      }

      const firstItem = parsed[0];

      if (firstItem && typeof firstItem === 'object' && firstItem !== null) {
        const tmpl = generateTemplateFromJSON(parsed, 'response_data');
        if (tmpl && tmpl.trim()) {
          const fieldCount = Object.keys(firstItem).length;
          console.log(
            '[PreviewTesterModal] 🎯 Generated array (Case 1) template with',
            fieldCount,
            'top-level fields (delegated to generateTemplateFromJSON)'
          );
          return tmpl;
        }
      }

      // Array of primitives → generic loop template
      const tmpl = `{% for item in response_data %}\n    {{ item }}\n{% endfor %}`;
      console.log('[PreviewTesterModal] 🎯 Generated primitive array template');
      return tmpl;
    }

    // ============================================================
    // OBJECT — check for nested arrays
    // ============================================================
    const hasNestedArray = Object.values(parsed).some(
      (v) =>
        Array.isArray(v) &&
        v.length > 0 &&
        typeof v[0] === 'object' &&
        v[0] !== null
    );

    if (hasNestedArray) {
      console.log(
        '[PreviewTesterModal] 🔔 Object has nested arrays — DEFAULT so LeftPanel can show layout popup'
      );
      return DEFAULT_TEMPLATE;
    }

    // ============================================================
    // FLAT OBJECT → Case 2 field template
    //
    // Delegate to generateTemplateFromJSON too. It handles nested
    // objects and primitive fields correctly. But we've already
    // established there are no nested arrays, so the output is
    // equivalent to the previous hand-written version.
    // ============================================================
    const keys = Object.keys(parsed);
    if (keys.length > 0) {
      const tmpl = generateTemplateFromJSON(parsed, 'response_data');
      if (tmpl && tmpl.trim()) {
        console.log(
          '[PreviewTesterModal] 🎯 Generated flat object (Case 2) template with',
          keys.length,
          'top-level fields (delegated to generateTemplateFromJSON)'
        );
        return tmpl;
      }
    }

    console.log('[PreviewTesterModal] ⚠️ Falling back to DEFAULT');
    return DEFAULT_TEMPLATE;
  };


  if (!isOpen) return null;

  const isValidSample = sampleResponse && sampleResponse.trim().length > 0;
  const initialTemplateStr = getInitialTemplateForPreview();

  console.log('[PreviewTesterModal] Rendering with:', {
    isValidSample,
    sampleResponseLength: sampleResponse?.length || 0,
    statusCode,
    kbId,
    initialTemplateStr: initialTemplateStr?.substring(0, 100),
    isTemplateReady,
    isClosing,
  });

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.2s ease',
      }}
    >
      <style>
        {`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        `}
      </style>

      <div
        ref={modalRef}
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '1400px',
          width: '95%',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          animation: 'slideUp 0.3s ease',
          position: 'relative',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '2px solid #e2e8f0',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <h3 style={{ margin: 0, fontSize: '20px', color: '#2d3748' }}>
              🎨 Create Response Template for {statusCode}
            </h3>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: '14px',
                color: '#718096',
              }}
            >
              Design your template using the Preview Tester. Click "Use Template" when done.
            </p>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#a0aec0',
              padding: '4px 8px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Config info bar */}
        <div
          style={{
            padding: '10px 16px',
            backgroundColor: '#f7fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#4a5568',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            flexShrink: 0,
          }}
        >
          <span>
            <strong>KB ID:</strong> {kbId || 'N/A'}
          </span>
          <span>
            <strong>Status:</strong> {statusCode}
          </span>
          {sampleResponse && (
            <span style={{ color: '#48bb78' }}>
              ✅ Sample response loaded ({sampleResponse.length} chars)
            </span>
          )}
          {isTemplateReady && (
            <span style={{ color: '#9f7aea', fontWeight: '600' }}>
              ✅ Template designed
            </span>
          )}
        </div>

        {/* Preview Tester container */}
        <div
          className="preview-tester-wrapper"
          style={{
            flex: 1,
            minHeight: '400px',
            overflow: 'auto',
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            backgroundColor: '#f7fafc',
            position: 'relative',
          }}
        >
          {isValidSample ? (
            <div style={{ height: '100%', minHeight: '500px' }}>
              <PreviewTester
                // ✅ Stable key per statusCode
                key={`preview-tester-${statusCode}`}
                ref={previewTesterRef}
                sampleResponse={sampleResponse}
                initialTemplate={initialTemplateStr}
                kbId={kbId}
                statusCode={statusCode}
                onSave={handlePreviewTesterSave}
                onChange={handlePreviewTesterChange}
                appData={appData}
                modalMode={true}
                onTemplateDataReady={handleTemplateDataReady}
                mode="template_creation"
                showControls={true}
                showScenarioTree={true}
                showTransformationPanel={true}
                forceSampleLoad={true}
                preserveTransformationState={true}
              />
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                color: '#a0aec0',
                textAlign: 'center',
                padding: '40px',
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  color: '#4a5568',
                }}
              >
                No Sample Response Available
              </div>
              <div
                style={{
                  fontSize: '14px',
                  marginTop: '8px',
                  maxWidth: '500px',
                  color: '#718096',
                }}
              >
                Please provide a sample response for status {statusCode} in the
                "Sample Response" textarea above.
              </div>
              <button
                onClick={onClose}
                style={{
                  marginTop: '20px',
                  padding: '8px 24px',
                  backgroundColor: '#4299e1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Go Back
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '16px',
            borderTop: '1px solid #e2e8f0',
            marginTop: '16px',
            flexShrink: 0,
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ fontSize: '13px', color: '#718096' }}>
            {isTemplateReady ? (
              <span style={{ color: '#48bb78' }}>
                ✅ Template designed. Click "Use Template" to apply.
              </span>
            ) : (
              <span>
                💡 Design your template using the Preview Tester above. The
                sample response is pre-loaded.
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleClose}
              style={{
                padding: '8px 20px',
                backgroundColor: 'transparent',
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleUseTemplate}
              disabled={!isTemplateReady}
              style={{
                padding: '8px 20px',
                backgroundColor: isTemplateReady ? '#9f7aea' : '#a0aec0',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isTemplateReady ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              ✅ Use Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewTesterModal;